import React, { useEffect } from 'react';
import { X, FileText } from 'lucide-react';
import { useI18n } from '../hooks/useI18n';
import { useOnboardingTour } from '../hooks/useOnboardingTour';

interface InvoiceTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (invoiceType: string) => void;
  invoiceTypes: string[];
}

const InvoiceTypeModal: React.FC<InvoiceTypeModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  invoiceTypes
}) => {
  const { t, isRTL } = useI18n();
  const { startTour } = useOnboardingTour();

  // Auto-start tour when modal opens for the first time
  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(() => {
      startTour({
        tourId: 'invoice-type-modal',
        steps: [
          {
            element: '#invoice-type-modal-header',
            popover: {
              title: t('onboarding.invoiceTypeModal.headerTitle'),
              description: t('onboarding.invoiceTypeModal.headerDesc'),
              side: 'bottom' as const,
              align: 'start' as const,
            },
          },
          {
            element: '#invoice-type-modal-list',
            popover: {
              title: t('onboarding.invoiceTypeModal.listTitle'),
              description: t('onboarding.invoiceTypeModal.listDesc'),
              side: 'top' as const,
              align: 'start' as const,
            },
          },
        ],
        autoStartDelay: 0,
      });
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSelect = (invoiceType: string) => {
    onSelect(invoiceType);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`bg-white rounded-lg shadow-xl max-w-md w-full mx-4 ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
        {/* Header */}
        <div id="invoice-type-modal-header" className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className={`flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-3'}`}>
            <FileText className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">{t('invoiceTypeModal.selectInvoiceType')}</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-gray-600 mb-6">
            {t('invoiceTypeModal.description')}
          </p>

          <div id="invoice-type-modal-list" className="space-y-3">
            {invoiceTypes.map((type) => (
              <button
                key={type}
                onClick={() => handleSelect(type)}
                className="w-full p-4 text-left border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors group"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-gray-900 group-hover:text-blue-700">
                      {t(`invoiceEdit.types.${type}`)}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {getInvoiceTypeDescription(type, t)}
                    </p>
                  </div>
                  <div className="text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            {t('invoiceTypeModal.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
};

// Helper function to get descriptions for invoice types
const getInvoiceTypeDescription = (type: string, t: any): string => {
  switch (type) {
    case 'INVOICE':
      return t('invoiceTypeModal.invoiceDescription');
    case 'QUOTE':
      return t('invoiceTypeModal.quoteDescription');
    case 'PROFORMA':
      return t('invoiceTypeModal.proformaDescription');
    case 'CREDIT_NOTE':
      return t('invoiceTypeModal.creditNoteDescription');
    case 'DEBIT_NOTE':
      return t('invoiceTypeModal.debitNoteDescription');
    default:
      return t('invoiceTypeModal.defaultDescription');
  }
};

export default InvoiceTypeModal;
